import { createRouter, createWebHistory } from 'vue-router'
import routes from './routes'
import swal from 'sweetalert2';
import state from '@/state';
import { isTokenExpired } from '@/utils/auth';

let lastTokenCheck = null;
const TOKEN_CHECK_INTERVAL = 30000;

const router = createRouter({
    history: createWebHistory(),
    mode: 'history',
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition
        } else {
            return { top: 0 }
        }
    }
});

router.beforeEach((to, from, next) => {
    // Define o título da página
    if (to.path === '/' || to.name === 'dashboard') {
        document.title = 'Lixo do Bem';
    } else if (to.name) {
        const routeName = to.name.charAt(0).toUpperCase() + to.name.slice(1);
        document.title = `Lixo do Bem | ${routeName}`;
    } else {
        document.title = 'Lixo do Bem';
    }

    next();
});

// Já logado não precisa ver o login de novo
router.beforeEach((to, from, next) => {
    if (to.meta.guestOnly) {
        const { user_logged, access_token } = state.getters['users/get_user_data'];

        if (user_logged && access_token) {
            return next({ name: 'dashboard' });
        }
    }

    next();
});

// Controle de acesso: precisa estar logado e com a sessão válida
router.beforeEach(async (to, from, next) => {
    if (!to.meta.requireAuth) {
        return next();
    }

    try {
        const userData = state.getters['users/get_user_data'];

        if (!userData.user_logged || !userData.access_token) {
            throw 'Para acessar esta funcionalidade, faça seu login na plataforma!';
        }

        // access_token vence em 1h. Sem refresh_token ("manter conectado" desligado) não há como renová-lo
        if (isTokenExpired(userData.access_token) && !userData.refresh_token) {
            lastTokenCheck = null;
            await state.dispatch('users/logout');
            throw 'Sua sessão expirou. Por favor, faça login novamente.';
        }

        const now = Date.now();
        const shouldCheckToken = !lastTokenCheck || (now - lastTokenCheck) > TOKEN_CHECK_INTERVAL;

        if (shouldCheckToken) {
            try {
                // GET /account valida a sessão (o interceptor faz o refresh se preciso)
                await state.dispatch('users/refresh_account');
                lastTokenCheck = now;
            } catch (error) {
                // sem internet não é motivo para derrubar a sessão
                if (!navigator.onLine) {
                    return next();
                }

                lastTokenCheck = null;
                await state.dispatch('users/logout');
                throw 'Sua sessão expirou. Por favor, faça login novamente.';
            }
        }

        // options (selects) são carregadas no login; se faltarem, busca de novo
        if (!state.getters['meta/get_options']) {
            await state.dispatch('meta/load_options');
        }

        next();
    } catch (issue) {
        const text = typeof issue === 'string' ? issue : 'Não foi possível validar sua sessão.';

        await swal.fire({
            title: 'Acesso restrito',
            text,
            icon: 'warning',
            confirmButtonText: 'Fazer login',
            customClass: {
                confirmButton: 'btn btn-primary'
            },
            buttonsStyling: false
        });

        next({ name: 'login', query: to.fullPath !== '/' ? { redirect: to.fullPath } : {} });
    }
});

// Depois do logout (por expiração ou manual), o próximo acesso valida a sessão do zero
router.afterEach((to) => {
    if (to.name === 'login') {
        lastTokenCheck = null;
    }
});

export default router;
